import Button from '../ui/Button'
import Input from '../ui/Input'
import { useLang } from '../../contexts/LangContext'
import { localized, localizedDescription } from '../../lib/i18n'
import type { OnboardingServiceCategory } from '../../graphql/providerOnboarding'
import type { OnboardingServiceDraft } from '../../lib/providerOnboarding'

interface ServicesStepProps {
  categories: OnboardingServiceCategory[]
  services: Record<string, OnboardingServiceDraft>
  saving: boolean
  onToggleService: (serviceTypeId: string) => void
  onChangeServiceField: (
    serviceTypeId: string,
    field: keyof Pick<OnboardingServiceDraft, 'customPriceEur' | 'customDurationMinutes'>,
    value: string,
  ) => void
  onBack: () => void
  onNext: () => void
}

function formatBasePrice(value: number, locale: 'el' | 'en') {
  return new Intl.NumberFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value / 100)
}

export default function ServicesStep({
  categories,
  services,
  saving,
  onToggleService,
  onChangeServiceField,
  onBack,
  onNext,
}: ServicesStepProps) {
  const { locale, t } = useLang()

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        {categories.length === 0 && (
          <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
            {t('Δεν υπάρχουν ακόμη ενεργές υπηρεσίες για επιλογή.', 'There are no active services to choose from yet.')}
          </div>
        )}

        {categories.map((category) => (
          <section key={category.id} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="max-w-2xl">
                <p className="inline-flex items-center gap-2 text-lg font-bold text-slate-950">
                  {category.icon ? <span>{category.icon}</span> : null}
                  <span>{localized(category, locale)}</span>
                </p>
                {localizedDescription(category, locale) && (
                  <p className="mt-2 text-sm leading-7 text-slate-600">
                    {localizedDescription(category, locale)}
                  </p>
                )}
              </div>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                {category.serviceTypes.length} {t('υπηρεσίες', 'services')}
              </span>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              {category.serviceTypes.map((serviceType) => {
                const draft = services[serviceType.id] ?? {
                  selected: false,
                  customPriceEur: '',
                  customDurationMinutes: '',
                }

                return (
                  <article
                    key={serviceType.id}
                    className={[
                      'rounded-[1.35rem] border p-4 transition-colors',
                      draft.selected ? 'border-primary-300 bg-white shadow-sm' : 'border-slate-200 bg-white/80',
                    ].join(' ')}
                  >
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        checked={draft.selected}
                        onChange={() => onToggleService(serviceType.id)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h3 className="text-base font-bold text-slate-950">{localized(serviceType, locale)}</h3>
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                            {formatBasePrice(serviceType.basePriceCents, locale)}
                          </span>
                        </div>
                        {localizedDescription(serviceType, locale) && (
                          <p className="mt-2 text-sm leading-6 text-slate-600">
                            {localizedDescription(serviceType, locale)}
                          </p>
                        )}
                        <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          {serviceType.estimatedDurationMinutes} {t('λεπτά βάση', 'minutes base')}
                        </p>
                      </div>
                    </label>

                    {draft.selected && (
                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <Input
                          type="text"
                          inputMode="decimal"
                          label={t('Προσαρμοσμένη τιμή (EUR)', 'Custom price (EUR)')}
                          placeholder={String(serviceType.basePriceCents / 100)}
                          value={draft.customPriceEur}
                          onChange={(event) => onChangeServiceField(serviceType.id, 'customPriceEur', event.target.value)}
                        />
                        <Input
                          type="number"
                          min="15"
                          step="15"
                          label={t('Διάρκεια (λεπτά)', 'Duration (minutes)')}
                          placeholder={String(serviceType.estimatedDurationMinutes)}
                          value={draft.customDurationMinutes}
                          onChange={(event) => onChangeServiceField(serviceType.id, 'customDurationMinutes', event.target.value)}
                        />
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button type="button" variant="secondary" size="lg" onClick={onBack}>
          {t('Πίσω', 'Back')}
        </Button>
        <Button type="button" size="lg" loading={saving} onClick={onNext}>
          {t('Αποθήκευση υπηρεσιών', 'Save services')}
        </Button>
      </div>
    </div>
  )
}
